(() => {
  const solve = (matrix, values) => {
    const rows = matrix.map((row, i) => [...row, values[i]]);
    for (let col = 0; col < values.length; col++) {
      let pivot = col;
      for (let i = col + 1; i < rows.length; i++) if (Math.abs(rows[i][col]) > Math.abs(rows[pivot][col])) pivot = i;
      [rows[col], rows[pivot]] = [rows[pivot], rows[col]];
      const divisor = rows[col][col];
      if (Math.abs(divisor) < 1e-10) throw new Error('Invalid mockup plane');
      rows[col] = rows[col].map(value => value / divisor);
      for (let i = 0; i < rows.length; i++) {
        if (i === col) continue;
        const factor = rows[i][col];
        rows[i] = rows[i].map((value, j) => value - factor * rows[col][j]);
      }
    }
    return rows.map(row => row.at(-1));
  };
  const homography = (rect, quad) => {
    const [x0, y0, x1, y1] = rect;
    const corners = [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
    const matrix = [], values = [];
    corners.forEach(([x, y], i) => {
      const [u, v] = quad[i];
      matrix.push([x, y, 1, 0, 0, 0, -u * x, -u * y], [0, 0, 0, x, y, 1, -v * x, -v * y]);
      values.push(u, v);
    });
    return [...solve(matrix, values), 1];
  };
  const project = (h, [x, y]) => {
    const denominator = h[6] * x + h[7] * y + 1;
    return [(h[0] * x + h[1] * y + h[2]) / denominator, (h[3] * x + h[4] * y + h[5]) / denominator];
  };
  const cssMatrix = h => `matrix3d(${[h[0], h[3], 0, h[6], h[1], h[4], 0, h[7], 0, 0, 1, 0, h[2], h[5], 0, 1].join(',')})`;
  const inverse = h => {
    const [a, b, c, d, e, f, g, j, k] = h;
    const result = [e * k - f * j, c * j - b * k, b * f - c * e,
      f * g - d * k, a * k - c * g, c * d - a * f,
      d * j - e * g, b * g - a * j, a * e - b * d];
    if (Math.abs(result[8]) < 1e-10) throw new Error('Invalid mockup plane');
    return result.map(value => value / result[8]);
  };
  const inset = (polygon, distance) => {
    const area = polygon.reduce((sum, p, i) => {
      const next = polygon[(i + 1) % polygon.length];
      return sum + p[0] * next[1] - next[0] * p[1];
    }, 0);
    const lines = polygon.map((p, i) => {
      const next = polygon[(i + 1) % polygon.length];
      const dx = next[0] - p[0], dy = next[1] - p[1], length = Math.hypot(dx, dy);
      const direction = area > 0 ? 1 : -1;
      const a = -dy / length * direction, b = dx / length * direction;
      return { a, b, c: a * p[0] + b * p[1] + distance };
    });
    return polygon.map((p, i) => {
      const previous = lines[(i + lines.length - 1) % lines.length], current = lines[i];
      const determinant = previous.a * current.b - current.a * previous.b;
      if (Math.abs(determinant) < 1e-9) return [p[0] + current.a * distance, p[1] + current.b * distance];
      return [(previous.c * current.b - current.c * previous.b) / determinant,
        (previous.a * current.c - current.a * previous.c) / determinant];
    });
  };
  async function rasterize(source, quad, { extendEdges = false } = {}) {
    const x = Math.floor(Math.min(...quad.map(p => p[0])));
    const y = Math.floor(Math.min(...quad.map(p => p[1])));
    const width = Math.ceil(Math.max(...quad.map(p => p[0]))) - x;
    const height = Math.ceil(Math.max(...quad.map(p => p[1]))) - y;
    const h = inverse(homography([0, 0, source.width, source.height], quad));
    const data = new Uint8ClampedArray(width * height * 4);
    for (let row = 0; row < height; row++) {
      const v = y + row + .5;
      for (let col = 0; col < width; col++) {
        const u = x + col + .5, den = h[6] * u + h[7] * v + 1;
        const sx = (h[0] * u + h[1] * v + h[2]) / den;
        const sy = (h[3] * u + h[4] * v + h[5]) / den;
        if (!extendEdges && (sx < 0 || sy < 0 || sx > source.width || sy > source.height)) continue;
        const px = Math.max(0, Math.min(source.width - 1, sx - .5));
        const py = Math.max(0, Math.min(source.height - 1, sy - .5));
        const x0 = Math.floor(px), y0 = Math.floor(py);
        const x1 = Math.min(x0 + 1, source.width - 1), y1 = Math.min(y0 + 1, source.height - 1);
        const fx = px - x0, fy = py - y0;
        const indices = [(y0 * source.width + x0) * 4, (y0 * source.width + x1) * 4,
          (y1 * source.width + x0) * 4, (y1 * source.width + x1) * 4];
        const weights = [(1 - fx) * (1 - fy), fx * (1 - fy), (1 - fx) * fy, fx * fy];
        const offset = (row * width + col) * 4;
        let alpha = 0, red = 0, green = 0, blue = 0;
        for (let i = 0; i < 4; i++) {
          const index = indices[i], weight = weights[i] * source.data[index + 3];
          alpha += weight; red += source.data[index] * weight;
          green += source.data[index + 1] * weight; blue += source.data[index + 2] * weight;
        }
        if (alpha > 0) {
          data[offset] = red / alpha; data[offset + 1] = green / alpha;
          data[offset + 2] = blue / alpha; data[offset + 3] = alpha;
        }
      }
      if (row % 96 === 95) await new Promise(resolve => setTimeout(resolve, 0));
    }
    return { x, y, width, height, data };
  }
  const paperSizes = Object.freeze([
    { name: 'A5', width: 14.8, height: 21 }, { name: 'A4', width: 21, height: 29.7 },
    { name: 'A3', width: 29.7, height: 42 }, { name: 'A2', width: 42, height: 59.4 },
    { name: 'A1', width: 59.4, height: 84.1 },
  ].map(size => Object.freeze(size)));
  function sizeLayout({ width, centerX = width / 2, baseline, type, size, availableSizes }) {
    const faceCm = type === 'filete' ? .6 : 1.85;
    const papers = availableSizes ? paperSizes.filter(paper => availableSizes.includes(paper.name)) : paperSizes;
    // The camera/table scale stays fixed when switching profile or availability.
    const pixelsPerCm = Math.min((width - 168) / (59.4 + 1.85 * 2), (baseline - 150) / (84.1 + 1.85 * 2));
    return papers.map(paper => {
      const outerWidth = (paper.width + faceCm * 2) * pixelsPerCm;
      const outerHeight = (paper.height + faceCm * 2) * pixelsPerCm;
      const item = { ...paper, x: centerX - outerWidth / 2, y: baseline - outerHeight, outerWidth, outerHeight,
        face: faceCm * pixelsPerCm, pixelsPerCm, opacity: paper.name === size ? 1 : .2 };
      return item;
    });
  }
  globalThis.PatasMockupGeometry = Object.freeze({ homography, project, cssMatrix, rasterize, inset, paperSizes, sizeLayout });
})();
